import { Popconfirm } from 'antd';
import { Formik } from 'formik';
import { type PropsWithChildren, forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';

import type { MeasureUnitIntFloatValue } from 'src/components/base/fields/SpecificationField/SpecificationIntFloatField/SpecificationIntFloatField.types';
import {
  type SpecificationIntFloatPopupFormValues,
  specificationIntFloatPopupFormValidationSchema,
} from 'src/components/base/fields/SpecificationField/SpecificationIntFloatField/SpecificationIntFloatPopup.validation';
import {
  SpecificationIntFloatPopupFormContent,
  type SpecificationIntFloatPopupFormContentRef,
} from 'src/components/base/fields/SpecificationField/SpecificationIntFloatField/SpecificationIntFloatPopupContent';
import { useModalExt } from 'src/hooks/useModalExt';
import type { SpecificationMeasureUnit } from 'src/types/reference/SpecificationMeasureUnit';

type SpecificationIntFloatPopupFormProps = {
  multiselect: boolean;
  defaultMeasureUnit: SpecificationMeasureUnit | null;
  allowedMeasureUnits: SpecificationMeasureUnit[];

  minValue: number | null;
  maxValue: number | null;

  onSubmit: (values: SpecificationIntFloatPopupFormValues) => Promise<void> | void;
};

const SpecificationIntFloatPopupForm = forwardRef<
  SpecificationIntFloatPopupFormContentRef,
  SpecificationIntFloatPopupFormProps
>(
  (
    {
      multiselect,
      defaultMeasureUnit,
      allowedMeasureUnits,
      minValue,
      maxValue,
      onSubmit,
    }: Readonly<SpecificationIntFloatPopupFormProps>,
    ref
  ) => {
    const specificationIntFloatPopupFormContent = useRef<SpecificationIntFloatPopupFormContentRef>(null);

    useImperativeHandle<SpecificationIntFloatPopupFormContentRef, SpecificationIntFloatPopupFormContentRef>(
      ref,
      () => ({
        submit: specificationIntFloatPopupFormContent.current?.submit,
        isDisabled: specificationIntFloatPopupFormContent.current?.isDisabled,
      }),
      []
    );

    return (
      <Formik<SpecificationIntFloatPopupFormValues>
        initialValues={{
          values: [
            {
              value: null,
              measureUnit: defaultMeasureUnit,
            },
          ],
        }}
        validationSchema={specificationIntFloatPopupFormValidationSchema(minValue, maxValue)}
        onSubmit={onSubmit}
      >
        {(formikProps) => (
          <SpecificationIntFloatPopupFormContent
            ref={specificationIntFloatPopupFormContent}
            formikProps={formikProps}
            multiselect={multiselect}
            defaultMeasureUnit={defaultMeasureUnit}
            allowedMeasureUnits={allowedMeasureUnits}
          />
        )}
      </Formik>
    );
  }
);

type SpecificationIntFloatPopupProps = PropsWithChildren<{
  multiselect: boolean;
  defaultMeasureUnit?: SpecificationMeasureUnit;
  allowedMeasureUnits: SpecificationMeasureUnit[];

  minValue: number | null;
  maxValue: number | null;

  onClose?: () => void;
  onAddValue: (newValues: MeasureUnitIntFloatValue[]) => void;
}>;

export type SpecificationIntFloatPopupRef = {
  open: () => void;
  close: () => void;
};

export const SpecificationIntFloatPopup = forwardRef<SpecificationIntFloatPopupRef, SpecificationIntFloatPopupProps>(
  (
    { multiselect, defaultMeasureUnit, allowedMeasureUnits, minValue, maxValue, onAddValue, onClose, children },
    ref
  ) => {
    const specificationIntFloatPopupForm = useRef<SpecificationIntFloatPopupFormContentRef>(null);
    const { modalProps, onOpen, onClose: onModalClose } = useModalExt();

    const handleClose = useCallback(() => {
      onModalClose();

      onClose?.();
    }, [onModalClose, onClose]);

    const handleSuccess = useCallback(
      (values: SpecificationIntFloatPopupFormValues) => {
        onAddValue(values.values as MeasureUnitIntFloatValue[]);

        handleClose();
      },
      [onAddValue, handleClose]
    );

    const handleConfirm = useCallback(() => specificationIntFloatPopupForm.current?.submit?.(), []);

    useImperativeHandle<SpecificationIntFloatPopupRef, SpecificationIntFloatPopupRef>(
      ref,
      () => ({
        open: () => {
          onOpen();
        },
        close: () => {
          handleClose();
        },
      }),
      [onOpen, handleClose]
    );

    useEffect(() => {
      if (modalProps.open) {
        const clickHandler = (event: MouseEvent) => {
          const popupElement = document.getElementsByClassName('specification-int-float-popup')?.[0];

          if (
            popupElement &&
            // When press Remove item button on last element it becomes removed when this click handler fires and `popupElement.contains` is false
            document.contains(event.target as Element) &&
            (!(event.target instanceof Element) || !popupElement.contains(event.target))
          ) {
            window.removeEventListener('click', clickHandler);

            handleClose();
          }
        };

        // Even from click on Custom button is captured here. Need some time to avoid it
        setTimeout(() => window.addEventListener('click', clickHandler));

        return () => {
          window.removeEventListener('click', clickHandler);
        };
      }
    }, [modalProps.open, handleClose]);

    return (
      <Popconfirm
        placement="top"
        overlayClassName="specification-int-float-popup"
        open={modalProps.open}
        destroyTooltipOnHide={true}
        title="Add Custom Value"
        description={
          <SpecificationIntFloatPopupForm
            ref={specificationIntFloatPopupForm}
            multiselect={multiselect}
            defaultMeasureUnit={defaultMeasureUnit ?? null}
            allowedMeasureUnits={allowedMeasureUnits}
            minValue={minValue}
            maxValue={maxValue}
            onSubmit={handleSuccess}
          />
        }
        okText="Add"
        cancelText="Cancel"
        icon={null}
        onConfirm={handleConfirm}
        onCancel={handleClose}
        okButtonProps={
          specificationIntFloatPopupForm.current?.isDisabled != null
            ? {
                disabled: specificationIntFloatPopupForm.current.isDisabled,
              }
            : undefined
        }
      >
        {children}
      </Popconfirm>
    );
  }
);
